import { ZeroPage } from './next/ZeroGuide';
import './next/skin';
import './next/next.css';
/** Точка входа: только монтирование. Само дерево — в `ui/App`, чтобы его можно было проверить тестом. */

import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import { Gate } from './next/Gate';
import { Landing } from './next/Landing';
import { Look } from './next/Look';
import { UI_NEXT } from './next/mode';
import {
  LAB_BUNDLE,
  appHref,
  isReadyFeed,
  isApp,
  reportHref,
  isLook,
  isMid,
  isStudio,
  isReport,
  isCup,
  isCupRace,
  isCupRaceDemo,
  isDry,
  isPredict,
  isForecast,
  isRusskie,
  isTransfers,
  isUx,
  isUxCard,
  isAdmin,
  isCurator,
  isLearn,
  isLearn2,
  isUiCalcMock,
  isEvents,
} from './next/route';
import { isBook } from './book/cash/route';
import { parked } from './next/parked';
import { ReviewPage } from './next/ReviewPage';
import { Screen } from './next/Screen';
import './styles.css';
import { App } from './ui/App';
import { CardLab } from './ux/card/CardLab';
import { CalcLab } from './ux/calc/CalcLab';
import { UxScreen } from './ux/UxScreen';

// Касса доступна в обеих сборках отдельным чанком. Коридоры остаются в /lab.
const Book = lazy(async () => {
  const mod = await import('./book/cash/Cash');
  return { default: mod.Cash };
});
const Mid = LAB_BUNDLE
  ? lazy(async () => {
      const mod = await import('./next/Mid');
      return { default: mod.Mid };
    })
  : null;
// Студия — тоже отдельный чанк. Статический импорт тащил движок фильтров и её
// CSS в кадр ленты: jsdom-гейт /app исполнял их вместе со сканером, а выкат
// 10.09 откатил исправный фронт, когда в снапшоте не осталось строк, которые
// профиль «работа» показывает. На /app этот модуль не нужен.
const Studio = lazy(async () => {
  const mod = await import('./next/Studio');
  return { default: mod.Studio };
});

const Transfers = lazy(async () => {
  const mod = await import('./next/Transfers');
  return { default: mod.Transfers };
});
const Russkie = lazy(async () => {
  const mod = await import('./next/Russkie');
  return { default: mod.Russkie };
});
const Predict = lazy(async () => {
  const mod = await import('./next/Predict');
  return { default: mod.Predict };
});
const Experiments = lazy(async () => {
  const mod = await import('./next/Experiments');
  return { default: mod.Experiments };
});
const CupPage = lazy(async () => {
  const mod = await import('./next/Cup');
  return { default: mod.Cup };
});
const CupRacePage = lazy(async () => {
  const mod = await import('./next/CupRace');
  return { default: mod.CupRace };
});
const CupRaceDemoPage = lazy(async () => {
  const mod = await import('./next/CupRace');
  return { default: mod.CupRaceDemo };
});
const CallPage = lazy(async () => {
  const mod = await import('./calls/CallPage');
  return { default: mod.CallPage };
});
const Admin = lazy(async () => {
  const mod = await import('./next/admin/Admin');
  return { default: mod.Admin };
});
const Curator = lazy(async () => {
  const mod = await import('./next/curator/Curator');
  return { default: mod.Curator };
});
const Learn = lazy(async () => {
  const mod = await import('./next/learn/Learn');
  return { default: mod.Learn };
});
const Learn2 = lazy(async () => {
  const mod = await import('./next/learn/Learn');
  return { default: mod.Learn };
});
const Forecast = lazy(async () => {
  const mod = await import('./next/Forecast');
  return { default: mod.Forecast };
});
const Report = lazy(async () => {
  const mod = await import('./next/ReportPage');
  return { default: mod.ReportPage };
});
const Dry = lazy(async () => {
  const mod = await import('./next/DryPage');
  return { default: mod.DryPage };
});
const EventsPage = lazy(async () => {
  const mod = await import('./next/EventsPage');
  return { default: mod.EventsPage };
});
const FORECAST_OPEN = !parked.forecast || import.meta.env.VITE_FORECAST === '1';

// Второй корень включается переменной сборки, а не условием на клиенте: боевая
// сборка идёт без `VITE_UI` и получает прежнее дерево буква в букву.
//
// Внутри нового контура страниц: первая на корне, сканер на `/app`, студия
// на `/studio`. Касса доступна на `/book`, коридоры — только в бандле `/lab`.
//
// Рабочие экраны стоят за дверью (`Gate`): на контуре со входом невошедший
// получает первую страницу, а не урезанную ленту. Первая страница дверью не
// закрывается — она и есть дверь.
if (!LAB_BUNDLE && isMid(location.pathname)) {
  location.replace(appHref());
}
if (parked.tests && /(?:^|\/)app\/test$/.test(location.pathname.replace(/\/+$/, ''))) {
  location.replace(`${appHref()}${location.search}${location.hash}`);
}
if (parked.predict && isPredict(location.pathname)) {
  location.replace(`${appHref()}${location.search}${location.hash}`);
}
if (parked.book && isBook(location.pathname)) {
  location.replace(`${reportHref()}${location.search}${location.hash}`);
}
if (!FORECAST_OPEN && isForecast(location.pathname)) {
  location.replace(`${appHref()}${location.search}${location.hash}`);
}

const next =
  !parked.book && isBook(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Book />
      </Suspense>
    </Gate>
  ) : isReadyFeed(location.pathname) ? (
    <Gate><Suspense fallback={null}><Experiments /></Suspense></Gate>
  ) : isRusskie(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Russkie />
      </Suspense>
    </Gate>
  ) : isTransfers(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Transfers />
      </Suspense>
    </Gate>
  ) : !parked.predict && isPredict(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Predict />
      </Suspense>
    </Gate>
  ) : isDry(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Dry />
      </Suspense>
    </Gate>
  ) : isEvents(location.pathname) ? (
    <Suspense fallback={null}>
      <EventsPage />
    </Suspense>
  ) : isReport(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Report />
      </Suspense>
    </Gate>
  ) : isStudio(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Studio />
      </Suspense>
    </Gate>
  ) : LAB_BUNDLE && Mid !== null && isMid(location.pathname) ? (
    <Gate>
      <Suspense fallback={null}>
        <Mid />
      </Suspense>
    </Gate>
  ) : isApp(location.pathname) ? (
    <Gate>
      <Screen />
    </Gate>
  ) : isAdmin(location.pathname) ? (
    <Suspense fallback={null}>
      <Admin />
    </Suspense>
  ) : isCurator(location.pathname) ? (
    <Suspense fallback={null}>
      <Curator />
    </Suspense>
  ) : isLearn2(location.pathname) ? (
    <Suspense fallback={null}>
      <Learn2 />
    </Suspense>
  ) : isLearn(location.pathname) ? (
    <Suspense fallback={null}>
      <Learn />
    </Suspense>
  ) : isLook(location.pathname) ? (
    <Look />
  ) : (
    <Landing />
  );
// Макет /ux живёт вне флага VITE_UI: иначе старая сборка на корне его не видит,
// а нам он нужен и на проде, не трогая боевую ленту.
createRoot(document.getElementById('root')!).render(
  isCupRaceDemo(location.pathname) ? (
    <Gate><Suspense fallback={null}><CupRaceDemoPage /></Suspense></Gate>
  ) :
  isCupRace(location.pathname) ? (
    <Gate><Suspense fallback={null}><CupRacePage /></Suspense></Gate>
  ) :
  isCup(location.pathname) ? (
    <Suspense fallback={null}><CupPage /></Suspense>
  ) :
  location.pathname.replace(/\/+$/, '').endsWith('/zero-arbs') ? <ZeroPage /> :
  isForecast(location.pathname) ? (
    FORECAST_OPEN ? <Suspense fallback={null}><Forecast /></Suspense> : null
  ) :
  /^\/c(?:all)?\/[A-Za-z0-9_-]{24,64}$/.test(location.pathname) ? (
    <Gate><Suspense fallback={null}><CallPage /></Suspense></Gate>
  ) : /^\/r\/[A-Za-z0-9_-]{16,64}$/.test(location.pathname.replace(/\/+$/, '')) ? (
    <ReviewPage />
  ) : isUiCalcMock(location.pathname) ? (
    <CalcLab />
  ) : isUxCard(location.pathname) ? (
    <CardLab />
  ) : isUx(location.pathname) ? (
    <UxScreen />
  ) : (!parked.book && isBook(location.pathname)) || isReadyFeed(location.pathname) || isDry(location.pathname) || UI_NEXT ? (
    next
  ) : (
    <App />
  ),
);
